<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-radio-group
          :model-value="envelopeTypeFormField.$model"
          required
          :disabled="isEditingLimited"
          class="envelopeType__container"
          inline
          :error-messages="envelopeTypeErrorMessages"
          @update:model-value="envelopeTypeChange"
        >
          <div v-for="option in envelopeTypeOptions" :key="`option-${option.value}`" class="d-flex flex-column">
            <AppTooltip :key="option.value" :disabled="!option.disabled">
              <template #activator="{props}">
                <label v-bind="props" :for="`option-${option.value}`">
                  <v-radio :id="`option-${option.value}`" :value="option.value" class="envelopeType__card" :class="{
                      'envelopeType__card--disabled': option.disabled,
                      'envelopeType__card--active': option.value === envelopeTypeFormField.$model,
                    }" :disabled="option.disabled" :data-locator="`${option.value}-envelope-type-card`">
                    <template v-slot:label>
                      <div class="w-100 d-flex align-center justify-space-between">
                        <h4>{{
                          $t(`project.signing-checklist.add.step2.Section3.envelopeTypeOptions.${option.value.toLowerCase()}`)
                          }}</h4>
                        <span v-if="option.value === ENVELOPE_TYPES.SINGLE && isMultiEnvelopeChild">
                          ({{ $t('common.envelopeType.fromBulk') }})
                        </span>
                      </div>
                    </template>
                  </v-radio>
                </label>
              </template>
              <span>{{ $t('project.signing-checklist.add.step2.Section3.envelopeMultipleDSQOptionDisabledTooltip')
                }}</span>
            </AppTooltip>
            <span class="mt-2 text-originalGrey-darken-2">
              {{
              $t(`project.signing-checklist.add.step2.Section3.envelopeTypeOptions.${option.value.toLowerCase()}Info`)
              }}
            </span>
          </div>
        </v-radio-group>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { createNamespacedHelpers } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { formFieldErrors, VuelidateFormField } from '@/common/utils/form'
import { ENVELOPE_TYPES, SIGNATURE_TYPE_DOCUSIGN_QUALIFIED } from '@/project/signing-checklist/constants'
import { SET_IMPORTED_FILE_ENVELOPE_TYPE } from '@/store/modules/signing-checklist/mutation_types'

type EnvelopeTypeOption = {
  value: string
  disabled: boolean
}

type DataType = {
  ENVELOPE_TYPES: typeof ENVELOPE_TYPES
  envelopeTypeOptions: EnvelopeTypeOption[]
}

const { mapMutations: mapSigningChecklistMutations } = createNamespacedHelpers('signingChecklist')

export default defineComponent({
  name: 'Section3',
  components: { AppTooltip },
  props: {
    fileId: {
      type: Number,
      required: true,
    },
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
    isMultiEnvelopeChild: {
      type: Boolean,
      required: true,
    },
    envelopeTypeFormField: {
      type: Object as PropType<VuelidateFormField>,
      required: true,
    },
    signatureType: {
      type: String,
      required: true,
    },
  },
  data (): DataType {
    return {
      ENVELOPE_TYPES,
      envelopeTypeOptions: [],
    }
  },
  computed: {
    envelopeTypeErrorMessages (): Array<string> {
      return formFieldErrors(
        this.envelopeTypeFormField,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.signing-checklist.add.step2.Section3.envelopeTypeLabel') })],
        ],
      )
    },
  },
  methods: {
    ...mapSigningChecklistMutations([SET_IMPORTED_FILE_ENVELOPE_TYPE]),
    envelopeTypeChange (value: string | null) {
      this[SET_IMPORTED_FILE_ENVELOPE_TYPE]({
        fileId: this.fileId,
        value,
      })
    },
    setEnvelopeTypeOptions (): void {
      Object.values(ENVELOPE_TYPES).forEach(value => {
        const isDisabled = value === ENVELOPE_TYPES.MULTIPLE.toUpperCase() && this.signatureType === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED
        if (value !== ENVELOPE_TYPES.NDA) {
          this.envelopeTypeOptions.push({ value: value, disabled: isDisabled })
        }
      })
    },
  },
  mounted () {
    this.setEnvelopeTypeOptions()
  },
  watch: {
    signatureType (newValue) {
      this.envelopeTypeOptions = this.envelopeTypeOptions.map(env =>
        env.value === ENVELOPE_TYPES.MULTIPLE ? { ...env, disabled: newValue === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED } : env,
      )
    },
  },
})
</script>

<style scoped lang="scss">
.envelopeType__container {
  :deep(.v-selection-control-group) {
    width: 100%;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  }

  .envelopeType__card {
    width: 100%;
    justify-self: stretch;
    min-height: 72px;
    padding: 16px 24px;
    border: 1px solid #DEE2E6;
    border-radius: 4px;
    margin: 0;

    &--active {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: -2px;
      background-color: rgb(var(--v-theme-primary-lighten-4));
    }

    &--disabled {
      :deep(*) {
        color: #D4D7DA;
      }
    }

    &:not(&--disabled) {
      cursor: pointer;
    }

    .caption {
      color: #717B85;
      font-size: 12px;
    }
    :deep(.v-label){
      width: 100%;
    }
  }
}
</style>
